import { type FC, useState } from "react";
import {
  Toolbar,
  Checkbox,
  Button,
  Typography,
  Divider,
  Menu,
} from "@mui/material";
import { useSnackbar } from "notistack";
import {
  ActionMenuItem,
  ToolbarProgress,
  useConfirmAction,
} from "@/shared/components";
import { useTranslation } from "react-i18next";
import {
  useUpdateProductsMutation,
  useDeleteProductsMutation,
  type ProductsItem,
} from "@/partner/entities/products";

interface ProductsListToolbarProps {
  indeterminate: boolean;
  numSelected: number;
  nodes: ProductsItem[];
  onSelectAllClick: (event: React.ChangeEvent<HTMLInputElement>) => void;
  selectedItems: { id: string }[];
  onCloseSelected: () => void;
}

export const ProductsListToolbar: FC<ProductsListToolbarProps> = ({
  indeterminate,
  numSelected,
  nodes,
  onSelectAllClick,
  selectedItems,
  onCloseSelected,
}) => {
  const { t } = useTranslation();
  const { enqueueSnackbar } = useSnackbar();
  const { confirm, dialog: confirmDialog } = useConfirmAction();
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const open = Boolean(anchorEl);

  const handleClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const [updateProducts, { loading: updateLoading }] =
    useUpdateProductsMutation({
      onCompleted: () => {
        onCloseSelected();
        enqueueSnackbar(t("products.toolbar.updateSuccess"), {
          variant: "success",
        });
      },
      onError: (error) => {
        enqueueSnackbar(error.message, { variant: "error" });
      },
      refetchQueries: ["ProductsList"],
    });

  const [deleteProducts, { loading: deleteLoading }] =
    useDeleteProductsMutation({
      onCompleted: () => {
        onCloseSelected();
        enqueueSnackbar(t("products.toolbar.deleteSuccess"), {
          variant: "success",
        });
      },
      onError: (error) => {
        enqueueSnackbar(error.message, { variant: "error" });
      },
      refetchQueries: ["ProductsList"],
    });

  const onPublishSelected = () => {
    updateProducts({
      variables: {
        input: selectedItems.map((item) => ({
          id: item.id,
          publishAt: new Date().toISOString(),
        })),
      },
    });
  };

  const onHideSelected = () => {
    updateProducts({
      variables: {
        input: selectedItems.map((item) => ({
          id: item.id,
          publishAt: null,
        })),
      },
    });
  };

  const onDeleteSelected = () => {
    confirm({
      title: t("common.actions.delete"),
      text: t("products.toolbar.deleteConfirm"),
      // Промис наружу: диалог держится открытым с полосой прогресса,
      // пока идёт запрос, и закрывается сам по успеху.
      onConfirm: () =>
        deleteProducts({
          variables: {
            ids: selectedItems.map((item) => item.id),
          },
        }),
    });
  };

  return (
    <Toolbar sx={{ position: "relative" }}>
      <Checkbox
        color="default"
        indeterminate={indeterminate}
        checked={numSelected === nodes.length}
        onChange={onSelectAllClick}
        sx={{
          marginLeft: "-16px",
          marginRight: {
            xs: 0,
            sm: "16px",
          },
        }}
      />

      <Typography>
        {t("products.toolbar.selectedCount", { count: numSelected })}
      </Typography>

      <Divider
        orientation="vertical"
        variant="middle"
        sx={{
          mx: 1.25,
          height: "2em",
        }}
      />

      <Button
        color="inherit"
        onClick={handleClick}
        disabled={numSelected === 0}
      >
        {t("products.toolbar.actions")}
      </Button>

      <Menu
        id="products-actions-menu"
        anchorEl={anchorEl}
        open={open}
        onClose={handleClose}
      >
        <ActionMenuItem
          disabled={numSelected === 0}
          loading={updateLoading}
          onClick={() => {
            onPublishSelected();
            handleClose();
          }}
        >
          {t("products.toolbar.publish")}
        </ActionMenuItem>

        <ActionMenuItem
          disabled={numSelected === 0}
          loading={updateLoading}
          onClick={() => {
            onHideSelected();
            handleClose();
          }}
        >
          {t("products.toolbar.hide")}
        </ActionMenuItem>

        <ActionMenuItem
          disabled={numSelected === 0}
          loading={deleteLoading}
          onClick={() => {
            onDeleteSelected();
            handleClose();
          }}
        >
          {t("products.toolbar.delete")}
        </ActionMenuItem>
      </Menu>

      <ToolbarProgress loading={updateLoading || deleteLoading} />
      {confirmDialog}
    </Toolbar>
  );
};
